import { supabase } from '../supabase';

export interface BookingInvoiceTaxComponent {
  name: string;
  rate_bps: number;
  amount_cents: number;
  /** Taxable base this rate was applied to (Σ net of lines carrying this rate), in cents. */
  base_cents?: number;
}

export type BookingInvoiceStatus = 'draft' | 'open' | 'partially_paid' | 'paid' | 'void';

/**
 * Locked = issued (open / partially_paid / paid). Booking edits no longer flow
 * into a locked invoice, so billing surfaces must freeze services, products,
 * and discounts while this is true. Draft and void invoices are not locked.
 */
export function isInvoiceLocked(
  invoice: { status: BookingInvoiceStatus | string } | null | undefined,
): boolean {
  return !!invoice && invoice.status !== 'draft' && invoice.status !== 'void';
}

export interface BookingInvoice {
  id: string;
  booking_id: string | null;
  customer_id: string | null;
  invoice_number: string | null;
  status: BookingInvoiceStatus;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  amount_due_cents: number;
  tips_cents: number;
  surcharge_cents: number;
  tax_breakdown: BookingInvoiceTaxComponent[];
  payment_terms: string | null;
  due_at: string | null;
  finalized_at: string | null;
  /** Frozen bill-to snapshot (customer by default, overridable per invoice). */
  bill_to_name?: string | null;
  bill_to_email?: string | null;
  bill_to_phone?: string | null;
  bill_to_address?: string | null;
  /** Customer PO reference. Null until staff type one. */
  po_number?: string | null;
}

const INVOICE_SELECT =
  'id, booking_id, customer_id, invoice_number, status, currency, ' +
  'subtotal_cents, discount_cents, tax_cents, total_cents, ' +
  'amount_paid_cents, amount_due_cents, tips_cents, surcharge_cents, tax_breakdown, ' +
  'payment_terms, due_at, finalized_at, ' +
  'bill_to_name, bill_to_email, bill_to_phone, bill_to_address, po_number';

/**
 * Fetch the (latest) invoice row for a booking, or null if none exists yet
 * (e.g. legacy bookings created before the new invoice system). Never throws —
 * callers fall back to legacy booking totals when this returns null.
 */
export async function getInvoiceForBooking(bookingId: string): Promise<BookingInvoice | null> {
  const { data, error } = await supabase
    .from('invoices')
    .select(INVOICE_SELECT)
    .eq('booking_id', bookingId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;

  const invoiceId = (data as any).id as string;
  const headerBreakdown: BookingInvoiceTaxComponent[] = Array.isArray((data as any).tax_breakdown)
    ? (data as any).tax_breakdown
    : [];

  // Derive the taxable base per rate from the line items so the summary can show
  // "GST (5% on $X)". Each taxable line's net_cents counts toward every rate it carries.
  const baseByRate = await computeTaxBaseByRate(invoiceId);

  const tax_breakdown = headerBreakdown.map((c) => {
    const key = `${c.name}::${c.rate_bps}`;
    const base = baseByRate.get(key);
    return base != null ? { ...c, base_cents: base } : c;
  });

  return {
    ...(data as any),
    tax_breakdown,
  } as BookingInvoice;
}

interface InvoiceLineTaxRow {
  net_cents: number;
  taxable: boolean;
  tax_components: { name: string; rate_bps: number; amount_cents: number }[] | null;
}

/** Map of `${name}::${rate_bps}` → Σ net_cents of lines carrying that rate. */
async function computeTaxBaseByRate(invoiceId: string): Promise<Map<string, number>> {
  const result = new Map<string, number>();
  const { data, error } = await supabase
    .from('invoice_line_items')
    .select('net_cents, taxable, tax_components')
    .eq('invoice_id', invoiceId);

  if (error || !data) return result;

  for (const row of data as InvoiceLineTaxRow[]) {
    if (!row.taxable || !Array.isArray(row.tax_components)) continue;
    for (const comp of row.tax_components) {
      const key = `${comp.name}::${comp.rate_bps}`;
      result.set(key, (result.get(key) ?? 0) + (row.net_cents || 0));
    }
  }
  return result;
}
